import * as React from 'react';
import { Link } from 'react-router-dom';

import type { IAdminHubAffiliation, IAffiliation } from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import { useAdminData } from '../../components/admin-page';
import { ParticipantLink } from '../../components/participant-link';
import { ResponseStatusChip } from '../../components/responses-table';
import { responsePath } from '../../paths';
import { slotWords } from '../../roster-words';
import { resolvedByPhrase } from './roster';

/**
 * A person's teams and firms as the hub reads them (SR-414, record search §3.7–§3.8; the plan's
 * D23) — `hub.affiliations`, in survey-api's order: each team or firm linked under its own list,
 * how it reaches the person — "through their login" where an account of theirs acts for it, the
 * slot in words and how survey-api resolved it where a roster names them, both where both — and
 * its responses, newest change first, each its survey and cycle linked to the response with its
 * status. A team reached only by its roster is in no household, so this is the one place a
 * member's page names that team's survey (SR-413's Barbara Roberts).
 *
 * **One read, the hub's**: no read of its own, so nothing to wait for and nothing that fails apart
 * from the page. survey-api leaves an archived team or firm out, as the roster's memberships do,
 * and a slot resolved by nobody names nobody — so the empty sentence is "as read". The root keeps
 * `MembershipsFact`'s hook and heading: it stands where that stood, and says the same of a slot.
 */
export function AffiliationsFact({
  participantId,
  affiliations,
  search
}: {
  participantId: string;
  affiliations: readonly IAdminHubAffiliation[];
  search: string;
}) {
  return (
    <div data-memberships={participantId} data-affiliations className="grid gap-1">
      <p className="text-xs font-medium text-muted-foreground">Teams & firms</p>
      {affiliations.length === 0 ? (
        <p>{absent('Named on no team or firm.')}</p>
      ) : (
        <ul aria-label="Teams & firms" className="grid gap-2">
          {affiliations.map((affiliation) => (
            <li
              key={affiliation.participantId}
              data-affiliation={affiliation.participantId}
              className="grid gap-0.5"
            >
              <p>
                <ParticipantLink participant={affiliation} search={search} /> —{' '}
                {reachedBy(affiliation)}
              </p>
              {affiliation.responses.length === 0 ? (
                <p className="text-muted-foreground">No response held.</p>
              ) : (
                <ul aria-label={`${affiliation.displayName}’s responses`} className="grid gap-0.5">
                  {affiliation.responses.map((response) => (
                    <li key={response.responseId} className="flex flex-wrap items-center gap-2">
                      <Link
                        to={responsePath(response.responseId)}
                        className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
                      >
                        {response.survey.code} {response.cycle.code}
                      </Link>
                      <ResponseStatusChip status={response.status} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** How a team or firm reaches the person: their login, a slot of its roster, or both. */
export function reachedBy(affiliation: IAffiliation): string {
  const ways: string[] = [];
  if (affiliation.slot !== null && affiliation.role !== null) {
    const slot = slotWords({ slot: affiliation.slot, role: affiliation.role });
    ways.push(
      affiliation.resolvedBy === null ? slot : `${slot}, ${resolvedByPhrase(affiliation.resolvedBy)}`
    );
  }
  if (affiliation.viaAccount) ways.push('through their login');
  return ways.join('; ');
}

/**
 * Which teams and firms name a person (SR-222, F7's R7) — the `memberships` of survey-api's
 * `GET …/participants/{id}/roster`, a fact on the person's Overview card: each team or firm by
 * its name, linked to its hub, with the slot it names them in and how survey-api found them —
 * "Wexford Team — Advisor 3, by CRD".
 *
 * **The person's own read**, not the section's: a team's roster reads its slots, and only the
 * person's read says which slots elsewhere resolve to them. So it reads per person member, below a
 * loaded hub, and a team's or firm's card draws no such fact — its `memberships` is empty by
 * construction, a slot naming a person alone.
 *
 * **Always drawn on a person's card, in four states**: "Reading…" — text, since one line needs no
 * skeleton and the hub's specs count the kit's — the list, "Named on no team or firm", or the
 * failure said. The empty sentence is "as read", never "has never been": survey-api leaves out an
 * archived team or firm, and a slot resolved by nobody names nobody. Nothing is sorted or counted
 * here: the order is survey-api's, by display name.
 *
 * **Re-read on the page's `written` cue, never on the poll** (R3's reasons): a decision on a
 * team's slot, its archive or a correction moves a membership; a registry's answer does not.
 */
export function MembershipsFact({
  participantId,
  search,
  written,
  onCount
}: {
  participantId: string;
  search: string;
  written: number;
  /** Says how many teams and firms name the person, as the read lands — Related records' "(0)". */
  onCount?: (count: number) => void;
}) {
  const { data, error } = useAdminData(
    (signal) => participantsApi.getRoster(participantId, signal),
    [participantId, written]
  );
  const count = data?.memberships.length;
  React.useEffect(() => {
    if (count !== undefined) onCount?.(count);
  }, [count, onCount]);

  return (
    <div data-memberships={participantId} className="grid gap-1">
      <p className="text-xs font-medium text-muted-foreground">Teams & firms</p>
      {data === null ? (
        <p role={error ? 'alert' : undefined}>
          {absent(error ? `Could not read the teams and firms: ${error}` : 'Reading…')}
        </p>
      ) : (
        <>
          {error && (
            <p role="alert">
              {absent(`Could not refresh — showing them as last read: ${error}`)}
            </p>
          )}
          {data.memberships.length === 0 ? (
            <p>{absent('Named on no team or firm.')}</p>
          ) : (
            <ul aria-label="Teams & firms" className="grid gap-1">
              {data.memberships.map((membership) => (
                <li key={`${membership.participantId} ${membership.slot}`}>
                  <ParticipantLink participant={membership} search={search} /> —{' '}
                  {slotWords(membership)}, {resolvedByPhrase(membership.resolvedBy)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
